import type { ProbabilityImpact, RiskEntry, RiskLevel, RiskStatus, Task } from "./types";

export function riskLevel(r: RiskEntry): RiskLevel | null {
  const map: Record<string, number> = { Low: 1, Medium: 2, High: 3 };
  const pv = map[r.probability];
  const iv = map[r.impact];
  if (!pv || !iv) return null;
  const score = pv * iv;
  if (score >= 6) return "high";
  if (score >= 3) return "medium";
  return "low";
}

function aliasTokenRegex(alias: string): RegExp {
  const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^0-9A-Za-z])${escaped}([^0-9A-Za-z]|$)`);
}

/**
 * A code like "R005/R006" carries two aliases — match either as a whole token so
 * "R001" doesn't also match inside "R010".
 */
function riskMatchesText(riskId: string, text: string): boolean {
  if (!text) return false;
  return riskId.split("/").filter(Boolean).some((a) => aliasTokenRegex(a).test(text));
}

export function linkedActivitiesFor(code: string, tasks: Task[]): string[] {
  if (!code.split("/").filter(Boolean).length) return [];
  return tasks.filter((t) => riskMatchesText(code, t.risk)).map((t) => t.id);
}

/** Risk register entries whose code appears as a whole token in an activity's free-text risk field. */
export function risksLinkedInText(riskText: string, allRisks: RiskEntry[]): RiskEntry[] {
  return allRisks.filter((r) => riskMatchesText(r.id, riskText));
}

/** Appends a risk's code + description onto an activity's free-text risk field. */
export function addRiskReference(riskText: string, risk: RiskEntry): string {
  const segment = risk.desc ? `${risk.id} - ${risk.desc}` : risk.id;
  const trimmed = riskText.trim();
  return trimmed ? `${trimmed}; ${segment}` : segment;
}

/** Removes the segment referencing a risk from an activity's free-text risk field. */
export function removeRiskReference(riskText: string, risk: RiskEntry): string {
  return riskText
    .split(";")
    .map((s) => s.trim())
    .filter((s) => s && !riskMatchesText(risk.id, s))
    .join("; ");
}

/** The register's closed vocabularies — shared by the Risk tab and the edit modal's
 *  risk sub-tab, which offer the same fields against the same entries. */
export const PROBABILITY_IMPACT_OPTIONS: ProbabilityImpact[] = ["", "Low", "Medium", "High"];
export const RISK_STATUS_OPTIONS: RiskStatus[] = ["Needs input", "Open", "Mitigated", "Monitoring", "Closed"];
